'use client';

import { useEffect, useState } from 'react';
import { FileText, Shield } from 'lucide-react';
import Link from '@/components/compat/Link';
import Navbar from '@/components/help-center/Navbar';
import Footer from '@/components/help-center/Footer';
import { HeroBackdropLayers, cn } from '@/components/help-center/latest-power-shared';
import { getGuideHref } from '@/lib/guide-routes';
import {
  formatPrivacidadeLastUpdated,
  PRIVACIDADE_INTRO,
  PRIVACIDADE_LAST_UPDATED_ISO,
  PRIVACIDADE_SECTIONS,
} from '@/lib/privacidade-content';

const CONTENT_LINK_CLASS =
  'font-medium text-[#000b6a] underline decoration-[#000b6a]/25 underline-offset-2 transition-colors hover:text-[#000b6a]/80 hover:decoration-[#000b6a]/50';

export default function PrivacidadePage() {
  const lastUpdatedLabel = formatPrivacidadeLastUpdated(PRIVACIDADE_LAST_UPDATED_ISO);
  const [activeId, setActiveId] = useState(PRIVACIDADE_SECTIONS[0]?.id ?? '');

  useEffect(() => {
    const nodes = PRIVACIDADE_SECTIONS.map((s) => document.getElementById(s.id)).filter(
      (el): el is HTMLElement => Boolean(el)
    );
    if (nodes.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const top = visible[0]?.target.id;
        if (top) setActiveId(top);
      },
      { rootMargin: '-20% 0px -55% 0px', threshold: [0.1, 0.35, 0.6] }
    );

    for (const node of nodes) observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 bg-[#F4F7FC]">
        <section className="relative overflow-hidden border-b border-blue-900/50 bg-[#030023]">
          <HeroBackdropLayers className="absolute inset-0" />
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#030023]"
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-0 opacity-40" aria-hidden>
            <span className="absolute left-[12%] top-[28%] h-1 w-1 rounded-full bg-white/80 shadow-[0_0_6px_rgba(255,255,255,0.8)]" />
            <span className="absolute left-[78%] top-[34%] h-0.5 w-0.5 rounded-full bg-sky-100/70" />
            <span className="absolute left-[22%] top-[62%] h-0.5 w-0.5 rounded-full bg-white/60" />
            <span className="absolute left-[88%] top-[58%] h-1 w-1 rounded-full bg-white/50 shadow-[0_0_8px_rgba(125,211,252,0.5)]" />
            <span className="absolute left-[55%] top-[42%] h-0.5 w-0.5 rounded-full bg-white/50" />
          </div>

          <div className="relative z-10 mx-auto max-w-7xl px-6 pb-12 pt-28 sm:pb-14 sm:pt-32 md:pb-16 md:pt-36">
            <div className="mb-4 sm:mb-5">
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border border-sky-400/25',
                  'bg-sky-500/10 px-3 py-1 font-inter text-[10px] font-bold uppercase tracking-[0.16em] text-sky-100/90',
                  'backdrop-blur-sm sm:text-[11px]'
                )}
              >
                <Shield className="h-3 w-3 text-sky-300" aria-hidden />
                Privacidade
              </span>
            </div>

            <div className="max-w-2xl">
              <h1 className="font-poppins text-[2rem] font-bold leading-[1.15] tracking-tight text-white sm:text-4xl md:text-5xl">
                Política de Privacidade
              </h1>
              <p className="mt-4 max-w-xl font-poppins text-lg leading-relaxed text-[#B5BEDA] md:text-xl">
                Entenda como as informações podem ser tratadas quando você utiliza o xat Ajuda.
              </p>
            </div>
          </div>
        </section>

        <section className="relative pb-20 pt-10 sm:pb-24 sm:pt-14" aria-label="Política de Privacidade">
          <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] lg:gap-14 xl:grid-cols-[minmax(0,15rem)_minmax(0,42rem)_minmax(0,1fr)]">
            <aside className="lg:sticky lg:top-28 lg:self-start">
              <p className="mb-3 font-poppins text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                Nesta página
              </p>
              <nav
                aria-label="Secções da Política de Privacidade"
                className={cn(
                  '-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1',
                  'lg:mx-0 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:px-0 lg:pb-0',
                  '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
                )}
              >
                {PRIVACIDADE_SECTIONS.map((section) => {
                  const active = activeId === section.id;
                  return (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className={cn(
                        'shrink-0 rounded-full px-3 py-1.5 font-poppins text-[12px] font-medium transition-colors',
                        'lg:rounded-lg lg:px-2.5 lg:py-1.5 lg:text-[13px]',
                        active
                          ? 'bg-[rgba(0,11,106,0.08)] text-[#000b6a]'
                          : 'text-slate-500 hover:bg-slate-200/60 hover:text-slate-800'
                      )}
                    >
                      {section.navLabel}
                    </a>
                  );
                })}
              </nav>
            </aside>

            <article className="min-w-0 max-w-3xl">
              <header className="mb-10 border-b border-[rgba(0,11,106,0.1)] pb-8">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[rgba(0,11,106,0.12)] bg-white px-3 py-1.5 shadow-[0_1px_0_rgba(0,11,106,0.04)]">
                  <FileText className="h-3.5 w-3.5 text-[#000b6a]/70" aria-hidden />
                  <span className="font-inter text-[12px] text-slate-600">
                    Última atualização:{' '}
                    <time
                      dateTime={PRIVACIDADE_LAST_UPDATED_ISO}
                      className="font-medium text-slate-800"
                    >
                      {lastUpdatedLabel}
                    </time>
                  </span>
                </div>

                <h2 className="font-poppins text-2xl font-bold tracking-tight text-slate-900 sm:text-[1.75rem]">
                  Política de Privacidade
                </h2>
                <p className="mt-4 max-w-2xl font-inter text-[15px] leading-relaxed text-slate-600 sm:text-base">
                  {PRIVACIDADE_INTRO}
                </p>
              </header>

              <div className="space-y-12 sm:space-y-14">
                {PRIVACIDADE_SECTIONS.map((section) => (
                  <section
                    key={section.id}
                    id={section.id}
                    className="scroll-mt-28"
                    aria-labelledby={`${section.id}-title`}
                  >
                    <h3
                      id={`${section.id}-title`}
                      className="font-poppins text-lg font-semibold tracking-tight text-slate-900 sm:text-xl"
                    >
                      {section.title}
                    </h3>
                    <div className="mt-4 space-y-3.5">
                      {section.paragraphs.map((paragraph) => (
                        <p
                          key={paragraph.slice(0, 48)}
                          className="font-inter text-[15px] leading-relaxed text-slate-600 sm:text-base"
                        >
                          {paragraph}
                        </p>
                      ))}
                      {section.bullets && section.bullets.length > 0 ? (
                        <ul className="mt-1 list-disc space-y-2.5 pl-5 font-inter text-[15px] leading-relaxed text-slate-600 sm:text-base">
                          {section.bullets.map((item) => (
                            <li key={item} className="pl-0.5">
                              {item}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {section.id === 'contato' ? (
                        <ul className="mt-5 flex flex-col gap-2 font-inter text-[15px] sm:text-base">
                          <li>
                            <Link href="/equipe" className={CONTENT_LINK_CLASS}>
                              Página da Equipe
                            </Link>
                          </li>
                          <li>
                            <Link href="/termos" className={CONTENT_LINK_CLASS}>
                              Termos de Uso
                            </Link>
                          </li>
                          <li>
                            <Link href="/regras" className={CONTENT_LINK_CLASS}>
                              Regras do chat
                            </Link>
                          </li>
                          <li>
                            <Link href="/conduta" className={CONTENT_LINK_CLASS}>
                              Código de Conduta
                            </Link>
                          </li>
                          <li>
                            <Link
                              href={getGuideHref('troubleshooting', 'sistema-de-tickets-do-xat')}
                              className={CONTENT_LINK_CLASS}
                            >
                              Sistema de tickets do xat
                            </Link>
                          </li>
                        </ul>
                      ) : null}
                    </div>
                  </section>
                ))}
              </div>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
